import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { Tooltip } from "primereact/tooltip";

import { CONTENT_TYPE_OPTIONS } from "../../../constants";
import type { AssessmentLessonRecommendation } from "../../../client/svcPla";
import { sanitizeTestId } from "../../../utils/string";

import type {
  SummaryTopicCardHeaderTemplateProps,
  TopicGroup,
  TopicScore,
} from "./summaryTopicCardTypes";

import S2 from "./SummaryTopicCard.new.module.scss";
import { C } from "../../utils";

type TopicLessonRecommendationProps = {
  groups: TopicGroup[];
  recommendationsByPath: Record<string, AssessmentLessonRecommendation>;
  onLessonStart: (lessonKey: string) => void;
};

export function LessonRecommendationCard({
  groups,
  recommendationsByPath,
  onLessonStart,
}: TopicLessonRecommendationProps) {
  return (
    <div className={S2.topicWrapperColumn}>
      {groups
        .filter(group => group.topics.length > 0)
        .map(group => (
          <Panel
            key={group.id}
            toggleable
            header={group.title}
            headerTemplate={props => <ScoreCategoryHeaderTemplate {...props} />}
            pt={{
              content: { className: S2.categoryContent },
            }}
            expandIcon="pi pi-angle-left"
            collapseIcon="pi pi-angle-down"
          >
            {group.topics.map(topicScore => {
              const topicId = topicScore.topicId ?? topicScore.topic;
              const recommendation =
                group.showRecommendations && topicScore.topicId
                  ? recommendationsByPath[topicScore.topicId]
                  : undefined;

              if (!recommendation) {
                return (
                  <div key={topicId} className={S2.topicItem}>
                    <div className={S2.topicItemTitle}>
                      <i className={`pi ${group.icon}`} />
                      {topicScore.topic}
                    </div>
                  </div>
                );
              }

              return (
                <RecommendationTopicRow
                  key={topicId}
                  topic={topicScore}
                  icon={group.icon}
                  recommendation={recommendation}
                  onLessonStart={onLessonStart}
                />
              );
            })}
          </Panel>
        ))}
    </div>
  );
}

type RecommendationTopicRowProps = {
  topic: TopicScore;
  icon: string;
  recommendation: AssessmentLessonRecommendation;
  onLessonStart: (lessonKey: string) => void;
};

const RecommendationTopicRow = ({
  topic,
  icon,
  recommendation,
  onLessonStart,
}: RecommendationTopicRowProps) => {
  const details = formatRecommendationDetails(recommendation);
  const hasLessonDetails = recommendation.lessonName || recommendation.lessonDescription;
  const lessonDetailsTooltipId = `lesson-details-${sanitizeTestId(topic.topicId ?? topic.topic)}`;

  return (
    <div className={S2.topicItem}>
      <div className={S2.topicItemTitle}>
        <i className={`pi ${icon}`} />
        {topic.topic}
      </div>
      <div className={S2.lessonRecommendation}>
        {recommendation.lessonKey && (
          <>
            <Button
              id={hasLessonDetails ? lessonDetailsTooltipId : undefined}
              size="small"
              label={recommendation.completed ? "Retake lesson" : "Start lesson"}
              severity={recommendation.completed ? "secondary" : undefined}
              onClick={() => onLessonStart(recommendation.lessonKey!)}
            />
            {hasLessonDetails && (
              <Tooltip
                target={`#${lessonDetailsTooltipId}`}
                position="left"
                className={S2.lessonDetailsTooltip}
              >
                <div className="flex flex-col gap-4">
                  {recommendation.lessonName && (
                    <h4 className="atlas-h5 font-bold">{recommendation.lessonName}</h4>
                  )}
                  {recommendation.lessonDescription && (
                    <em className={C("body-sm", S2.lessonDetailsDescription)}>
                      {recommendation.lessonDescription}
                    </em>
                  )}
                  {details && <p className="body-sm text-muted-aaa">{details}</p>}
                </div>
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ScoreCategoryHeaderTemplate = ({
  onTogglerClick,
  titleElement,
  togglerClassName,
  togglerElement,
}: SummaryTopicCardHeaderTemplateProps) => (
  <div className={S2.categoryHeader} onClick={onTogglerClick}>
    <div className={S2.categoryTitle}>
      <h3 className="atlas-h5">{titleElement}</h3>
      <div className={togglerClassName}>{togglerElement}</div>
    </div>
  </div>
);

const formatRecommendationDetails = (recommendation?: AssessmentLessonRecommendation) => {
  if (!recommendation) {
    return "";
  }

  const lessonType = CONTENT_TYPE_OPTIONS.find(
    option => option.value === recommendation.lessonType
  )?.label;

  const duration =
    typeof recommendation.duration === "number" ? `${recommendation.duration} minutes` : undefined;

  return [lessonType, duration].filter((detail): detail is string => Boolean(detail)).join(" · ");
};
